import React, { useState, useEffect } from 'react';
import { Modal, Steps, Select, Button, Table, Space, Tooltip, Checkbox, Spin, Popconfirm, Row, Col, Typography, Tag, Card } from 'antd';
import { SwapRightOutlined, SettingOutlined, CheckCircleOutlined, UserOutlined } from '@ant-design/icons';
import { getRequest, postRequest, ResponseDataType } from 'api';
import { toast } from 'react-toastify';
import type { ColumnsType } from 'antd/es/table';
import { ExceptionModal } from './ExceptionModal';

const { Step } = Steps;
const { Option } = Select;
const { Text, Title } = Typography;

export interface ClassYearDataType {
    classYearId: number;
    classId: number;
    classLevel: string;
    className: string;
    schoolId: number;
    studentCount?: number;
    status?: number;
}

export interface StudentDataType {
    studentId: number;
    studentCode: string;
    name: string;
}

export interface StudentExceptionItem {
    studentId: number;
    newClassYearId?: number | null;
    retained: boolean;
    dropOut: boolean;
}

export interface ClassMappingItem {
    oldClassYearId: number;
    newClassYearId: number | null;
}

interface PromotionModalProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    schoolId: number;
    initialYear: number;
}

export const PromotionModal: React.FC<PromotionModalProps> = ({ open, onClose, onSuccess, schoolId, initialYear }) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [loading, setLoading] = useState(false);
    
    // Step 1 State
    const [oldYear, setOldYear] = useState<number>(initialYear);
    const [newYear, setNewYear] = useState<number>(initialYear + 1);
    
    // Data State
    const [oldClassYears, setOldClassYears] = useState<ClassYearDataType[]>([]);
    const [newClassYears, setNewClassYears] = useState<ClassYearDataType[]>([]);
    
    // Mapping State
    const [classMappings, setClassMappings] = useState<ClassMappingItem[]>([]);
    const [exceptions, setExceptions] = useState<StudentExceptionItem[]>([]);
    
    // Preview State
    const [previewData, setPreviewData] = useState<any>(null);
    const [transferRevenueBlock, setTransferRevenueBlock] = useState<boolean>(true);

    // Exception Modal State
    const [exceptionModalOpen, setExceptionModalOpen] = useState(false);
    const [selectedOldClassForException, setSelectedOldClassForException] = useState<ClassYearDataType | null>(null);

    useEffect(() => {
        if (open) {
            setOldYear(initialYear);
            setNewYear(initialYear + 1);
            setCurrentStep(0);
            setClassMappings([]);
            setExceptions([]);
            setPreviewData(null);
            setTransferRevenueBlock(true);
            fetchClassesForYears(initialYear, initialYear + 1);
        }
    }, [open, initialYear]);

    const fetchClassesForYears = async (oldY: number, newY: number) => {
        setLoading(true);
        try {
            // Fetch old classes
            const oldRes: ResponseDataType<{ list: ClassYearDataType[] }> = await getRequest(
                `/cms/class/get-class-years-by-start-end-schoolId?start=${oldY}&schoolId=${schoolId}&perPage=1000`
            );
            // Fetch new classes
            const newRes: ResponseDataType<{ list: ClassYearDataType[] }> = await getRequest(
                `/cms/class/get-class-years-by-start-end-schoolId?start=${newY}&schoolId=${schoolId}&perPage=1000`
            );

            const oldList = oldRes?.info?.list || [];
            const newList = newRes?.info?.list || [];

            setOldClassYears(oldList);
            setNewClassYears(newList);

            // Initialize empty mappings
            const initialMappings = oldList.map(oldClass => ({
                oldClassYearId: oldClass.classYearId,
                newClassYearId: null
            }));
            setClassMappings(initialMappings);

        } catch (error) {
            toast.error('Lỗi khi tải danh sách lớp');
        } finally {
            setLoading(false);
        }
    };

    const handleYearChange = (type: 'old' | 'new', val: number) => {
        if (type === 'old') {
            setOldYear(val);
            fetchClassesForYears(val, newYear);
        } else {
            setNewYear(val);
            fetchClassesForYears(oldYear, val);
        }
    };

    const handleMappingChange = (oldClassYearId: number, newClassYearId: number | null) => {
        setClassMappings(prev => prev.map(m => 
            m.oldClassYearId === oldClassYearId ? { ...m, newClassYearId } : m
        ));
    };

    const handleOpenExceptions = (oldClass: ClassYearDataType) => {
        setSelectedOldClassForException(oldClass);
        setExceptionModalOpen(true);
    };

    const handleSaveExceptions = (newExceptionsForClass: StudentExceptionItem[]) => {
        // Remove old exceptions for this class (merge by studentId)
        setExceptions(prev => {
            const studentIdsToUpdate = new Set(newExceptionsForClass.map(e => e.studentId));
            const filteredPrev = prev.filter(e => !studentIdsToUpdate.has(e.studentId));
            return [...filteredPrev, ...newExceptionsForClass];
        });
        setExceptionModalOpen(false);
    };

    const handlePreview = async () => {
        const validMappings = classMappings.filter(m => m.newClassYearId != null);
        if (validMappings.length === 0) {
            toast.warn('Vui lòng ghép ít nhất 1 lớp để tiếp tục');
            return;
        }

        setLoading(true);
        try {
            const payload = {
                classMappings: validMappings,
                exceptions: exceptions
            };
            
            const res: ResponseDataType<any> = await postRequest('/cms/class/promotion-preview', payload);
            if (res.code === 200 && res.info) {
                setPreviewData(res.info);
                setCurrentStep(2);
            } else {
                toast.error(res.msg || 'Lỗi khi xem trước dữ liệu');
            }
        } catch (err) {
            toast.error('Lỗi kết nối khi xem trước');
        } finally {
            setLoading(false);
        }
    };

    const handleExecute = async () => {
        const validMappings = classMappings.filter(m => m.newClassYearId != null);
        
        setLoading(true);
        try {
            const payload = {
                classMappings: validMappings,
                exceptions: exceptions,
                transferRevenueBlock: transferRevenueBlock
            };
            
            const res: ResponseDataType<any> = await postRequest('/cms/class/promote', payload);
            if (res.code === 200) {
                toast.success('Chuyển lớp đồng loạt thành công!');
                onSuccess();
            } else {
                toast.error(res.msg || 'Lỗi khi thực thi chuyển lớp');
            }
        } catch (err) {
            toast.error('Lỗi kết nối khi thực thi');
        } finally {
            setLoading(false);
        }
    };

    const mappingColumns: ColumnsType<ClassYearDataType> = [
        {
            title: 'Lớp năm cũ',
            dataIndex: 'className',
            key: 'oldClass',
            width: '30%',
            render: (text, record) => <b>{text} (Khối {record.classLevel})</b>
        },
        {
            title: '',
            key: 'arrow',
            width: '5%',
            align: 'center',
            render: () => <SwapRightOutlined style={{ fontSize: 20, color: '#1890ff' }} />
        },
        {
            title: 'Lớp năm mới (Đích đến)',
            key: 'newClass',
            width: '40%',
            render: (_, record) => {
                const mapping = classMappings.find(m => m.oldClassYearId === record.classYearId);
                return (
                    <Select
                        showSearch
                        allowClear
                        placeholder="Chọn lớp đích"
                        style={{ width: '100%' }}
                        value={mapping?.newClassYearId}
                        onChange={(val) => handleMappingChange(record.classYearId, val)}
                        filterOption={(input, option) =>
                            (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
                        }
                        options={newClassYears.map(nc => ({
                            value: nc.classYearId,
                            label: `${nc.className} (Khối ${nc.classLevel})`
                        }))}
                    />
                );
            }
        },
        {
            title: 'Hành động',
            key: 'actions',
            width: '25%',
            align: 'center',
            render: (_, record) => {
                return (
                    <Button 
                        type="dashed" 
                        icon={<SettingOutlined />} 
                        onClick={() => handleOpenExceptions(record)}
                    >
                        Cấu hình ngoại lệ
                    </Button>
                );
            }
        }
    ];

    return (
        <Modal
            title="Lên lớp đồng loạt (Student Promotion)"
            open={open}
            onCancel={onClose}
            width={1000}
            footer={null}
            destroyOnClose
            maskClosable={false}
        >
            <Steps current={currentStep} style={{ marginBottom: 24 }}>
                <Step title="Chọn Năm học" description="Xác định chu kỳ" />
                <Step title="Ghép lớp" description="Cấu hình luồng di chuyển" />
                <Step title="Xem trước & Chốt" description="Xác nhận dữ liệu" />
            </Steps>

            <Spin spinning={loading}>
                {currentStep === 0 && (
                    <div style={{ padding: '20px 0', textAlign: 'center' }}>
                        <Space size="large">
                            <div>
                                <div style={{ marginBottom: 8, fontWeight: 'bold' }}>Năm học cũ (Nguồn)</div>
                                <Select value={oldYear} onChange={(v) => handleYearChange('old', v)} style={{ width: 150 }}>
                                    {[initialYear - 1, initialYear, initialYear + 1, initialYear + 2].map(y => (
                                        <Option key={y} value={y}>{y} - {y + 1}</Option>
                                    ))}
                                </Select>
                            </div>
                            <SwapRightOutlined style={{ fontSize: 24, marginTop: 24, color: '#888' }} />
                            <div>
                                <div style={{ marginBottom: 8, fontWeight: 'bold' }}>Năm học mới (Đích)</div>
                                <Select value={newYear} onChange={(v) => handleYearChange('new', v)} style={{ width: 150 }}>
                                    {[initialYear, initialYear + 1, initialYear + 2, initialYear + 3].map(y => (
                                        <Option key={y} value={y}>{y} - {y + 1}</Option>
                                    ))}
                                </Select>
                            </div>
                        </Space>
                        <div style={{ marginTop: 40 }}>
                            <Button type="primary" onClick={() => setCurrentStep(1)}>Tiếp tục</Button>
                        </div>
                    </div>
                )}

                {currentStep === 1 && (
                    <div>
                        <div style={{ marginBottom: 16 }}>
                            <Text type="danger">
                                <b>Lưu ý:</b> Lựa chọn lớp năm mới (đích đến). Bỏ trống ô (Clear) đối với các lớp đã ra trường (ví dụ Khối 12) hoặc các lớp giải tán.
                            </Text>
                        </div>
                        <Table
                            columns={mappingColumns}
                            dataSource={oldClassYears}
                            rowKey="classYearId"
                            pagination={false}
                            size="small"
                            bordered
                            scroll={{ y: 400 }}
                        />
                        <div style={{ marginTop: 20, textAlign: 'right' }}>
                            <Button style={{ marginRight: 8 }} onClick={() => setCurrentStep(0)}>Quay lại</Button>
                            <Button type="primary" onClick={handlePreview}>Xem trước</Button>
                        </div>
                    </div>
                )}

                {currentStep === 2 && previewData && (
                    <div>
                        <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
                            <Col span={6}>
                                <Card size="small" style={{ textAlign: 'center', background: '#f0f5ff', borderColor: '#adc6ff' }}>
                                    <div style={{ color: '#2f54eb' }}>Tổng số học sinh</div>
                                    <Title level={3} style={{ margin: 0, color: '#1d39c4' }}>{previewData.totalStudents}</Title>
                                </Card>
                            </Col>
                            <Col span={6}>
                                <Card size="small" style={{ textAlign: 'center', background: '#f6ffed', borderColor: '#b7eb8f' }}>
                                    <div style={{ color: '#52c41a' }}>Lên lớp thành công</div>
                                    <Title level={3} style={{ margin: 0, color: '#389e0d' }}>{previewData.totalPromoted}</Title>
                                </Card>
                            </Col>
                            <Col span={6}>
                                <Card size="small" style={{ textAlign: 'center', background: '#fff7e6', borderColor: '#ffd591' }}>
                                    <div style={{ color: '#fa8c16' }}>Lưu ban</div>
                                    <Title level={3} style={{ margin: 0, color: '#d46b08' }}>{previewData.totalRetained}</Title>
                                </Card>
                            </Col>
                            <Col span={6}>
                                <Card size="small" style={{ textAlign: 'center', background: '#fff1f0', borderColor: '#ffa39e' }}>
                                    <div style={{ color: '#f5222d' }}>Dừng học</div>
                                    <Title level={3} style={{ margin: 0, color: '#cf1322' }}>{previewData.totalDropOut}</Title>
                                </Card>
                            </Col>
                        </Row>
                        
                        {previewData.unmappedStudents && previewData.unmappedStudents.length > 0 && (
                            <div style={{ marginBottom: 16 }}>
                                <Text type="danger">
                                    <UserOutlined /> Cảnh báo: Có <b>{previewData.totalUnmapped}</b> học sinh bị rớt lại do lớp cũ không được ghép với lớp mới nào. Những học sinh này sẽ ở trạng thái "Chờ xếp lớp".
                                </Text>
                            </div>
                        )}

                        <div style={{ marginTop: 16, marginBottom: 16, padding: '10px 16px', background: '#f0f5ff', borderRadius: 6, border: '1px solid #adc6ff' }}>
                            <Checkbox
                                checked={transferRevenueBlock}
                                onChange={(e) => setTransferRevenueBlock(e.target.checked)}
                            >
                                <span style={{ fontWeight: 600, color: '#1d39c4' }}>
                                    Chuyển khối thu sang lớp mới (Khối Bán trú, Ngoại trú...)
                                </span>
                                <div style={{ fontSize: 12, color: '#595959', marginLeft: 24 }}>
                                    Học sinh được lên lớp sẽ tự động kế thừa Khối thu của năm cũ để phục vụ thu phí tháng.
                                </div>
                            </Checkbox>
                        </div>

                        <Table
                            title={() => <b>Dự kiến sĩ số năm học mới</b>}
                            dataSource={previewData.newClasses}
                            rowKey="newClassYearId"
                            pagination={false}
                            size="small"
                            bordered
                            columns={[
                                { title: 'Tên lớp mới', dataIndex: 'newClassName', key: 'newClassName' },
                                { title: 'Sĩ số dự kiến', dataIndex: 'expectedSize', key: 'expectedSize', align: 'center', render: (val) => <Tag color="blue">{val} HS</Tag> }
                            ]}
                        />

                        <div style={{ marginTop: 20, textAlign: 'right' }}>
                            <Button style={{ marginRight: 8 }} onClick={() => setCurrentStep(1)}>Quay lại chỉnh sửa</Button>
                            <Popconfirm
                                title="Chốt danh sách và thực thi?"
                                description="Thao tác này sẽ cập nhật dữ liệu của toàn bộ học sinh."
                                onConfirm={handleExecute}
                                okText="Thực thi"
                                cancelText="Hủy"
                            >
                                <Button type="primary" icon={<CheckCircleOutlined />}>Chốt & Lưu</Button>
                            </Popconfirm>
                        </div>
                    </div>
                )}
            </Spin>

            {exceptionModalOpen && selectedOldClassForException && (
                <ExceptionModal
                    open={exceptionModalOpen}
                    oldClass={selectedOldClassForException}
                    newClassYears={newClassYears}
                    existingExceptions={exceptions}
                    onClose={() => setExceptionModalOpen(false)}
                    onSave={handleSaveExceptions}
                />
            )}
        </Modal>
    );
};
